/**
 * Server-side client portal PIN gate (link + PIN, no customer login).
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, ensureAuth } from './firebase';

const functions = getFunctions(undefined, 'us-central1');

export type PortalAccessMeta = {
  portalId: string;
  slug: string;
  clientName: string;
  requiresPin: boolean;
};

export async function getPortalAccessMeta(slug: string): Promise<PortalAccessMeta> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'getPortalAccessMeta');
  const res = await callable({ slug });
  return res.data as PortalAccessMeta;
}

/**
 * Grant portalPinVerified claim after PIN check.
 * Pass empty pin only for portals with requiresPin=false (server enforces).
 */
export async function verifyPortalPinServer(slug: string, pin: string = ''): Promise<PortalAccessMeta> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'verifyPortalPin');
  const res = await callable({ slug, pin });
  // Force-refresh so Firestore rules see portalPinVerified / portalAccessPortalId.
  await auth.currentUser?.getIdToken(true);
  return res.data as PortalAccessMeta;
}

/** Persist selectedServices via Admin SDK (nested projects rows + top-level). */
export async function savePortalSelectedServices(input: {
  portalId: string;
  selectedServices: string[];
  projectId?: string | null;
}): Promise<void> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'savePortalSelectedServices');
  await callable({
    portalId: input.portalId,
    selectedServices: input.selectedServices,
    projectId: input.projectId || null,
  });
}

export type EngagementLetterPayload = {
  id: string;
  leadId?: string | null;
  dealId?: string | null;
  slug?: string | null;
  letterType?: string | null;
  title?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  projectDescription?: string | null;
  designFee?: number | null;
  deliverables?: string[];
  termsAndConditions?: string | null;
  status?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  sentAt?: string | null;
  viewedAt?: string | null;
  signedAt?: string | null;
  paidAt?: string | null;
  signature?: {
    dataUrl?: string | null;
    signedBy?: string | null;
    signedAt?: string | null;
  } | null;
  paymentId?: string | null;
  signedPdfUrl?: string | null;
  serverVerified?: boolean;
  signedVia?: string | null;
};

/** Guests cannot list engagementLetters — resolve by slug via callable. */
export async function getEngagementLetterBySlug(
  slug: string,
  opts?: { markViewed?: boolean; portalId?: string | null },
): Promise<EngagementLetterPayload> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'getEngagementLetterBySlug');
  const res = await callable({
    slug,
    markViewed: opts?.markViewed !== false,
    portalId: opts?.portalId || null,
  });
  return res.data as EngagementLetterPayload;
}

/** Guests cannot list proposalBuilders — resolve by slug via callable. */
export async function getProposalBuilderBySlug(
  slug: string,
  opts?: { stakeholderToken?: string | null },
): Promise<{ id: string; proposal: Record<string, unknown> }> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'getProposalBuilderBySlug');
  const res = await callable({
    slug,
    stakeholderToken: opts?.stakeholderToken || null,
  });
  return res.data as { id: string; proposal: Record<string, unknown> };
}

/** Resolve letter for a portal without guest list queries (by portalId / leadId). */
export async function getEngagementLetterForPortal(
  portalId: string,
): Promise<EngagementLetterPayload> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'getEngagementLetterForPortal');
  const res = await callable({ portalId });
  return res.data as EngagementLetterPayload;
}

/** Persist questionnaireStatus (incl. non-primary project rows) via Admin SDK. */
export async function savePortalQuestionnaireStatus(input: {
  portalId: string;
  questionnaireStatus: string;
  projectId?: string | null;
}): Promise<void> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'savePortalQuestionnaireStatus');
  await callable({
    portalId: input.portalId,
    questionnaireStatus: input.questionnaireStatus,
    projectId: input.projectId || null,
  });
}

/** Server-verified engagement letter signature (sets serverVerified for trigger). */
export async function signEngagementLetterServer(input: {
  letterId: string;
  signedBy: string;
  dataUrl: string;
}): Promise<{ letterId: string; status: string; serverVerified: boolean }> {
  await ensureAuth();
  const callable = httpsCallable(functions, 'signEngagementLetter');
  const res = await callable(input);
  return res.data as { letterId: string; status: string; serverVerified: boolean };
}
